import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRight, Eye, HandCoins, SlidersHorizontal } from 'lucide-react';

const EASE = [0.22, 1, 0.36, 1] as const;

const PILLARS = [
    { icon: Eye, title: 'Real rate visibility', body: 'See actual NET and direct rates — not a marked-up number with the real margin hidden behind it.' },
    { icon: HandCoins, title: 'Overrides passed back', body: 'The volume bonuses suppliers pay get passed back to you, not quietly kept off the top.' },
    { icon: SlidersHorizontal, title: 'You control your margin', body: 'On NET rates you set your own margin and refund policy. Your booking, your call.' },
];

interface AgentEconomicsProps {
    /** Opens the apply / live-demo request flow. */
    onRequestDemo?: () => void;
}

/**
 * "Why our advisors earn more" — the economics argument: the 100%-commission
 * reframe, overrides passed back, and an invitation to verify it in a live demo
 * with real bookings. Confident, not combative.
 */
export default function AgentEconomics({ onRequestDemo }: AgentEconomicsProps) {
    const shouldReduceMotion = useReducedMotion();

    return (
        <section className="agent-px bg-[#0b2222] py-20 sm:py-28">
            <div className="mx-auto max-w-5xl">
                <motion.div
                    initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-80px' }}
                    transition={{ duration: 0.7, ease: EASE }}
                    className="max-w-3xl"
                >
                    <span className="lux-caps lux-caps-lg mb-4 block text-[#F5F5F5]/60">Why Our Advisors Earn More</span>
                    <h2 className="lux-didot lux-didot-lg mb-6 text-[#F5F5F5]">Even after our split, our advisors earn more.</h2>

                    <p className="lux-body lux-body-lg mb-5 text-[#F5F5F5]/75">
                        Be careful with &ldquo;100% commission.&rdquo; It&rsquo;s 100% of a rate that&rsquo;s already been marked up and stripped of
                        its overrides before it ever reaches you. The honest question is: 100% of what?
                    </p>
                    <p className="lux-body lux-body-lg text-[#F5F5F5]/75">
                        We don&rsquo;t believe anyone should take a cut of your income without adding value. We pass overrides back, show you real NET
                        rates, and let you set your own margin &mdash; so most advisors who switch earn{' '}
                        <span className="text-[#F5F5F5]">30&ndash;50% more</span>, even after our split &mdash; which is 80/20 on your first $100K in
                        commission each year, then 90/10 after.
                    </p>
                </motion.div>

                <div className="mt-14 grid grid-cols-1 gap-10 md:grid-cols-3">
                    {PILLARS.map((p, i) => {
                        const Icon = p.icon;
                        return (
                            <motion.div
                                key={p.title}
                                initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, y: 16 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: '-40px' }}
                                transition={{ duration: 0.7, delay: i * 0.1, ease: EASE }}
                            >
                                <Icon className="mb-4 h-5 w-5 text-[#F5F5F5]/40" strokeWidth={1.5} />
                                <h3 className="lux-body lux-body-sm mb-2 font-medium text-[#F5F5F5]/90">{p.title}</h3>
                                <p className="lux-body lux-body-sm leading-relaxed text-[#F5F5F5]/60">{p.body}</p>
                            </motion.div>
                        );
                    })}
                </div>

                <motion.div
                    initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-40px' }}
                    transition={{ duration: 0.7, delay: 0.15, ease: EASE }}
                    className="mt-14 rounded-2xl border border-[#F5F5F5]/15 bg-[#0e2727] p-8 sm:p-10"
                >
                    <h3 className="lux-didot text-2xl text-[#F5F5F5] sm:text-3xl">Most advisors have never been shown a NET rate.</h3>
                    <p className="lux-body lux-body-sm mt-4 leading-relaxed text-[#F5F5F5]/70">
                        On a NET rate, we&rsquo;re the merchant of record &mdash; we take the payment, keep the commission, and pay the hotel. On a
                        standard commissionable rate, the supplier takes the payment and pays us later, often only after your client travels. Because we
                        hold the commission on NET rates, we pay you faster: a non-refundable NET booking lands on your{' '}
                        <span className="text-[#F5F5F5]">very next statement</span> &mdash; not months later. Payment in as little as 10 days.
                    </p>
                </motion.div>

                <motion.div
                    initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-40px' }}
                    transition={{ duration: 0.7, delay: 0.2, ease: EASE }}
                    className="mt-14 flex flex-col gap-8 rounded-2xl bg-[#f7f4ec] p-8 sm:p-10 md:flex-row md:items-center md:justify-between md:gap-12"
                >
                    <div className="max-w-2xl">
                        <span className="lux-caps lux-caps-sm mb-3 block text-[#0e2727]/45">See for yourself</span>
                        <h3 className="lux-didot text-2xl text-[#0e2727] sm:text-3xl">Don&rsquo;t take our word for it.</h3>
                        <p className="lux-body lux-body-sm mt-4 leading-relaxed text-[#0e2727]/70">
                            Already producing at another agency? Bring your own itineraries to a live demo with our VP of Business Development and shop
                            them in our system yourself, side by side with what you see today.
                        </p>
                        <p className="lux-body lux-body-sm mt-3 leading-relaxed text-[#0e2727]/45">
                            Live demos are reserved for advisors already in the business; new agents go hands-on with FastBook during training.
                        </p>
                    </div>
                    {onRequestDemo && (
                        <button
                            type="button"
                            onClick={onRequestDemo}
                            className="lux-caps lux-caps-sm group inline-flex shrink-0 items-center gap-2 rounded-full bg-[#0e2727] px-5 py-2.5 text-[#f7f4ec] transition-colors hover:bg-[#0b2222] sm:py-1.5"
                        >
                            Request a Live Demo
                            <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" strokeWidth={1.5} />
                        </button>
                    )}
                </motion.div>
            </div>
        </section>
    );
}
